import { z } from 'zod';

// RBAC Schemas
const PermissionSchema = z.object({
  resource: z.string(),
  action: z.enum(['read', 'write', 'delete', 'execute', 'admin']),
  conditions: z.record(z.string(), z.any()).optional()
});

const RoleSchema = z.object({
  id: z.string(),
  name: z.string(),
  description: z.string().optional(),
  permissions: z.array(PermissionSchema),
  inherits: z.array(z.string()).optional(),
  metadata: z.record(z.string(), z.any()).optional()
});

const UserRoleSchema = z.object({
  userId: z.string(),
  roleId: z.string(),
  tenantId: z.string().optional(),
  grantedAt: z.date(),
  grantedBy: z.string().optional(),
  expiresAt: z.date().optional()
});

export type Permission = z.infer<typeof PermissionSchema>;
export type Role = z.infer<typeof RoleSchema>;
export type UserRole = z.infer<typeof UserRoleSchema>;

// Predefined roles and permissions
export const SystemRoles = {
  SUPER_ADMIN: 'super_admin',
  ADMIN: 'admin',
  PROJECT_MANAGER: 'project_manager',
  SUPPORT_LEAD: 'support_lead',
  BUSINESS_ANALYST: 'business_analyst',
  SYSTEM_ARCHITECT: 'system_architect',
  DEVELOPER: 'developer',
  SUPPORT_AGENT: 'support_agent',
  VIEWER: 'viewer'
} as const;

export const SystemPermissions = {
  // Event management
  EVENTS_READ: { resource: 'events', action: 'read' as const },
  EVENTS_WRITE: { resource: 'events', action: 'write' as const },
  EVENTS_DELETE: { resource: 'events', action: 'delete' as const },
  EVENTS_REPLAY: { resource: 'events', action: 'execute' as const },

  // Workflow management
  WORKFLOWS_READ: { resource: 'workflows', action: 'read' as const },
  WORKFLOWS_WRITE: { resource: 'workflows', action: 'write' as const },
  WORKFLOWS_DELETE: { resource: 'workflows', action: 'delete' as const },
  WORKFLOWS_EXECUTE: { resource: 'workflows', action: 'execute' as const },

  // Search
  SEARCH_READ: { resource: 'search', action: 'read' as const },
  SEARCH_WRITE: { resource: 'search', action: 'write' as const },

  // Metrics and monitoring
  METRICS_READ: { resource: 'metrics', action: 'read' as const },
  METRICS_ADMIN: { resource: 'metrics', action: 'admin' as const },

  // Dead Letter Queue
  DLQ_READ: { resource: 'dlq', action: 'read' as const },
  DLQ_WRITE: { resource: 'dlq', action: 'write' as const },
  DLQ_EXECUTE: { resource: 'dlq', action: 'execute' as const },

  // System administration
  SYSTEM_ADMIN: { resource: 'system', action: 'admin' as const },
  USER_MANAGEMENT: { resource: 'users', action: 'admin' as const },
  ROLE_MANAGEMENT: { resource: 'roles', action: 'admin' as const },

  // Integration management
  INTEGRATIONS_READ: { resource: 'integrations', action: 'read' as const },
  INTEGRATIONS_WRITE: { resource: 'integrations', action: 'write' as const },
  INTEGRATIONS_EXECUTE: { resource: 'integrations', action: 'execute' as const },

  // Analytics
  ANALYTICS_READ: { resource: 'analytics', action: 'read' as const },
  ANALYTICS_WRITE: { resource: 'analytics', action: 'write' as const },
  ANALYTICS_ADMIN: { resource: 'analytics', action: 'admin' as const }
} as const;

// Predefined role definitions
export const PredefinedRoles: Record<string, Role> = {
  [SystemRoles.SUPER_ADMIN]: {
    id: SystemRoles.SUPER_ADMIN,
    name: 'Super Administrator',
    description: 'Full system access with all permissions',
    permissions: Object.values(SystemPermissions)
  },

  [SystemRoles.ADMIN]: {
    id: SystemRoles.ADMIN,
    name: 'Administrator',
    description: 'System administration with most permissions',
    permissions: [
      SystemPermissions.EVENTS_READ,
      SystemPermissions.EVENTS_WRITE,
      SystemPermissions.EVENTS_REPLAY,
      SystemPermissions.WORKFLOWS_READ,
      SystemPermissions.WORKFLOWS_WRITE,
      SystemPermissions.WORKFLOWS_EXECUTE,
      SystemPermissions.SEARCH_READ,
      SystemPermissions.SEARCH_WRITE,
      SystemPermissions.METRICS_READ,
      SystemPermissions.METRICS_ADMIN,
      SystemPermissions.DLQ_READ,
      SystemPermissions.DLQ_WRITE,
      SystemPermissions.DLQ_EXECUTE,
      SystemPermissions.INTEGRATIONS_READ,
      SystemPermissions.INTEGRATIONS_WRITE,
      SystemPermissions.INTEGRATIONS_EXECUTE,
      SystemPermissions.ANALYTICS_READ,
      SystemPermissions.ANALYTICS_WRITE,
      SystemPermissions.ANALYTICS_ADMIN
    ]
  },

  [SystemRoles.PROJECT_MANAGER]: {
    id: SystemRoles.PROJECT_MANAGER,
    name: 'Project Manager',
    description: 'Project management and workflow oversight',
    permissions: [
      SystemPermissions.EVENTS_READ,
      SystemPermissions.WORKFLOWS_READ,
      SystemPermissions.WORKFLOWS_WRITE,
      SystemPermissions.WORKFLOWS_EXECUTE,
      SystemPermissions.SEARCH_READ,
      SystemPermissions.METRICS_READ,
      SystemPermissions.ANALYTICS_READ,
      SystemPermissions.ANALYTICS_WRITE
    ]
  },

  [SystemRoles.SUPPORT_LEAD]: {
    id: SystemRoles.SUPPORT_LEAD,
    name: 'Support Lead',
    description: 'Support team leadership and ticket management',
    permissions: [
      SystemPermissions.EVENTS_READ,
      SystemPermissions.WORKFLOWS_READ,
      SystemPermissions.WORKFLOWS_WRITE,
      SystemPermissions.SEARCH_READ,
      SystemPermissions.METRICS_READ,
      SystemPermissions.DLQ_READ,
      SystemPermissions.DLQ_EXECUTE,
      SystemPermissions.ANALYTICS_READ
    ]
  },

  [SystemRoles.BUSINESS_ANALYST]: {
    id: SystemRoles.BUSINESS_ANALYST,
    name: 'Business Analyst',
    description: 'Data analysis and reporting',
    permissions: [
      SystemPermissions.EVENTS_READ,
      SystemPermissions.SEARCH_READ,
      SystemPermissions.METRICS_READ,
      SystemPermissions.ANALYTICS_READ,
      SystemPermissions.ANALYTICS_WRITE
    ]
  },

  [SystemRoles.SYSTEM_ARCHITECT]: {
    id: SystemRoles.SYSTEM_ARCHITECT,
    name: 'System Architect',
    description: 'System design and integration management',
    permissions: [
      SystemPermissions.EVENTS_READ,
      SystemPermissions.EVENTS_WRITE,
      SystemPermissions.WORKFLOWS_READ,
      SystemPermissions.WORKFLOWS_WRITE,
      SystemPermissions.SEARCH_READ,
      SystemPermissions.SEARCH_WRITE,
      SystemPermissions.METRICS_READ,
      SystemPermissions.INTEGRATIONS_READ,
      SystemPermissions.INTEGRATIONS_WRITE,
      SystemPermissions.INTEGRATIONS_EXECUTE,
      SystemPermissions.ANALYTICS_READ
    ]
  },

  [SystemRoles.DEVELOPER]: {
    id: SystemRoles.DEVELOPER,
    name: 'Developer',
    description: 'Development and testing access',
    permissions: [
      SystemPermissions.EVENTS_READ,
      SystemPermissions.WORKFLOWS_READ,
      SystemPermissions.WORKFLOWS_WRITE,
      SystemPermissions.SEARCH_READ,
      SystemPermissions.METRICS_READ,
      SystemPermissions.INTEGRATIONS_READ,
      SystemPermissions.INTEGRATIONS_WRITE
    ]
  },

  [SystemRoles.SUPPORT_AGENT]: {
    id: SystemRoles.SUPPORT_AGENT,
    name: 'Support Agent',
    description: 'Basic support operations',
    permissions: [
      SystemPermissions.EVENTS_READ,
      SystemPermissions.SEARCH_READ,
      SystemPermissions.METRICS_READ
    ]
  },

  [SystemRoles.VIEWER]: {
    id: SystemRoles.VIEWER,
    name: 'Viewer',
    description: 'Read-only access to basic information',
    permissions: [
      SystemPermissions.EVENTS_READ,
      SystemPermissions.SEARCH_READ,
      SystemPermissions.METRICS_READ
    ]
  }
};

// RBAC Service
export class RBACService {
  private static instance: RBACService;
  private roles: Map<string, Role> = new Map();
  private userRoles: Map<string, UserRole[]> = new Map();
  private customRoles: Map<string, Role> = new Map();

  private constructor() {
    this.initializeSystemRoles();
  }

  static getInstance(): RBACService {
    if (!RBACService.instance) {
      RBACService.instance = new RBACService();
    }
    return RBACService.instance;
  }

  private initializeSystemRoles(): void {
    // Add predefined roles
    Object.values(PredefinedRoles).forEach(role => {
      this.roles.set(role.id, role);
    });
  }

  // Role management
  createRole(role: Omit<Role, 'id'>): Role {
    const newRole: Role = {
      ...role,
      id: `role_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
    };

    this.customRoles.set(newRole.id, newRole);
    this.roles.set(newRole.id, newRole);

    console.log(`[RBACService] Created role: ${newRole.name} (${newRole.id})`);
    return newRole;
  }

  updateRole(roleId: string, updates: Partial<Role>): Role | null {
    const role = this.roles.get(roleId);
    if (!role) return null;

    const updatedRole = { ...role, ...updates };
    this.roles.set(roleId, updatedRole);

    if (this.customRoles.has(roleId)) {
      this.customRoles.set(roleId, updatedRole);
    }

    console.log(`[RBACService] Updated role: ${updatedRole.name} (${roleId})`);
    return updatedRole;
  }

  deleteRole(roleId: string): boolean {
    // Don't allow deletion of system roles
    if (Object.values(SystemRoles).includes(roleId as any)) {
      console.warn(`[RBACService] Cannot delete system role: ${roleId}`);
      return false;
    }

    const deleted = this.roles.delete(roleId) && this.customRoles.delete(roleId);
    
    if (deleted) {
      console.log(`[RBACService] Deleted role: ${roleId}`);
    }

    return deleted;
  }

  getRole(roleId: string): Role | null {
    return this.roles.get(roleId) || null;
  }

  getAllRoles(): Role[] {
    return Array.from(this.roles.values());
  }

  getCustomRoles(): Role[] {
    return Array.from(this.customRoles.values());
  }

  // User role management
  assignRoleToUser(userId: string, roleId: string, grantedBy?: string, tenantId?: string): boolean {
    const role = this.roles.get(roleId);
    if (!role) {
      console.warn(`[RBACService] Role not found: ${roleId}`);
      return false;
    }

    const userRole: UserRole = {
      userId,
      roleId,
      tenantId,
      grantedAt: new Date(),
      grantedBy,
      expiresAt: undefined
    };

    const userRoles = this.userRoles.get(userId) || [];
    userRoles.push(userRole);
    this.userRoles.set(userId, userRoles);

    console.log(`[RBACService] Assigned role ${roleId} to user ${userId}`);
    return true;
  }

  removeRoleFromUser(userId: string, roleId: string): boolean {
    const userRoles = this.userRoles.get(userId);
    if (!userRoles) return false;

    const initialLength = userRoles.length;
    const filteredRoles = userRoles.filter(ur => ur.roleId !== roleId);
    
    if (filteredRoles.length < initialLength) {
      this.userRoles.set(userId, filteredRoles);
      console.log(`[RBACService] Removed role ${roleId} from user ${userId}`);
      return true;
    }

    return false;
  }

  getUserRoles(userId: string): UserRole[] {
    return this.userRoles.get(userId) || [];
  }

  getUserEffectiveRoles(userId: string): Role[] {
    const userRoles = this.getUserRoles(userId);
    const effectiveRoles: Role[] = [];

    for (const userRole of userRoles) {
      const role = this.roles.get(userRole.roleId);
      if (role) {
        effectiveRoles.push(role);
        
        // Add inherited roles
        if (role.inherits) {
          for (const inheritedRoleId of role.inherits) {
            const inheritedRole = this.roles.get(inheritedRoleId);
            if (inheritedRole && !effectiveRoles.find(r => r.id === inheritedRole.id)) {
              effectiveRoles.push(inheritedRole);
            }
          }
        }
      }
    }

    return effectiveRoles;
  }

  // Permission checking
  hasPermission(userId: string, resource: string, action: Permission['action'], conditions?: Record<string, any>): boolean {
    const effectiveRoles = this.getUserEffectiveRoles(userId);
    
    for (const role of effectiveRoles) {
      for (const permission of role.permissions) {
        if (permission.resource === resource && permission.action === action) {
          // Check conditions if specified
          if (conditions && permission.conditions) {
            if (this.evaluateConditions(permission.conditions, conditions)) {
              return true;
            }
          } else if (!conditions) {
            return true;
          }
        }
      }
    }

    return false;
  }

  hasAnyPermission(userId: string, permissions: Permission[]): boolean {
    return permissions.some(permission => 
      this.hasPermission(userId, permission.resource, permission.action, permission.conditions)
    );
  }

  hasAllPermissions(userId: string, permissions: Permission[]): boolean {
    return permissions.every(permission => 
      this.hasPermission(userId, permission.resource, permission.action, permission.conditions)
    );
  }

  // Get user permissions
  getUserPermissions(userId: string): Permission[] {
    const effectiveRoles = this.getUserEffectiveRoles(userId);
    const permissions: Permission[] = [];

    for (const role of effectiveRoles) {
      for (const permission of role.permissions) {
        if (!permissions.find(p => p.resource === permission.resource && p.action === permission.action)) {
          permissions.push(permission);
        }
      }
    }

    return permissions;
  }

  // Condition evaluation
  private evaluateConditions(permissionConditions: Record<string, any>, userConditions: Record<string, any>): boolean {
    for (const [key, value] of Object.entries(permissionConditions)) {
      if (userConditions[key] !== value) {
        return false;
      }
    }
    return true;
  }

  // Role-based analytics views
  getAnalyticsViewPermissions(userId: string): {
    canViewProjectMetrics: boolean;
    canViewSupportMetrics: boolean;
    canViewCrossSystemMetrics: boolean;
    canViewAllViews: boolean;
    canExportData: boolean;
    canModifyDashboards: boolean;
  } {
    const permissions = this.getUserPermissions(userId);
    
    return {
      canViewProjectMetrics: this.hasPermission(userId, 'analytics', 'read') || 
                            this.hasAnyPermission(userId, [
                              SystemPermissions.ANALYTICS_READ,
                              { resource: 'analytics', action: 'read' as const, conditions: { view: 'project' } }
                            ]),
      
      canViewSupportMetrics: this.hasPermission(userId, 'analytics', 'read') || 
                            this.hasAnyPermission(userId, [
                              SystemPermissions.ANALYTICS_READ,
                              { resource: 'analytics', action: 'read' as const, conditions: { view: 'support' } }
                            ]),
      
      canViewCrossSystemMetrics: this.hasPermission(userId, 'analytics', 'read') || 
                                this.hasAnyPermission(userId, [
                                  SystemPermissions.ANALYTICS_READ,
                                  { resource: 'analytics', action: 'read' as const, conditions: { view: 'cross-system' } }
                                ]),
      
      canViewAllViews: this.hasPermission(userId, 'analytics', 'admin') || 
                      this.hasPermission(userId, 'system', 'admin'),
      
      canExportData: this.hasPermission(userId, 'analytics', 'write') || 
                    this.hasPermission(userId, 'analytics', 'admin'),
      
      canModifyDashboards: this.hasPermission(userId, 'analytics', 'admin') || 
                          this.hasPermission(userId, 'system', 'admin')
    };
  }

  // Workflow builder permissions
  getWorkflowBuilderPermissions(userId: string): {
    canCreateWorkflows: boolean;
    canEditWorkflows: boolean;
    canDeleteWorkflows: boolean;
    canExecuteWorkflows: boolean;
    canViewTemplates: boolean;
    canCreateTemplates: boolean;
  } {
    return {
      canCreateWorkflows: this.hasPermission(userId, 'workflows', 'write'),
      canEditWorkflows: this.hasPermission(userId, 'workflows', 'write'),
      canDeleteWorkflows: this.hasPermission(userId, 'workflows', 'delete'),
      canExecuteWorkflows: this.hasPermission(userId, 'workflows', 'execute'),
      canViewTemplates: this.hasPermission(userId, 'workflows', 'read'),
      canCreateTemplates: this.hasPermission(userId, 'workflows', 'write') && 
                         this.hasPermission(userId, 'workflows', 'admin')
    };
  }

  // Event management permissions
  getEventManagementPermissions(userId: string): {
    canViewEvents: boolean;
    canReplayEvents: boolean;
    canDeleteEvents: boolean;
    canViewDLQ: boolean;
    canRetryDLQ: boolean;
    canClearDLQ: boolean;
  } {
    return {
      canViewEvents: this.hasPermission(userId, 'events', 'read'),
      canReplayEvents: this.hasPermission(userId, 'events', 'execute'),
      canDeleteEvents: this.hasPermission(userId, 'events', 'delete'),
      canViewDLQ: this.hasPermission(userId, 'dlq', 'read'),
      canRetryDLQ: this.hasPermission(userId, 'dlq', 'execute'),
      canClearDLQ: this.hasPermission(userId, 'dlq', 'write')
    };
  }

  // Bulk operations
  bulkAssignRoles(userRoleAssignments: Array<{ userId: string; roleId: string; grantedBy?: string; tenantId?: string }>): {
    success: string[];
    failed: Array<{ userId: string; roleId: string; error: string }>;
  } {
    const result = {
      success: [] as string[],
      failed: [] as Array<{ userId: string; roleId: string; error: string }>
    };

    for (const assignment of userRoleAssignments) {
      try {
        const success = this.assignRoleToUser(
          assignment.userId,
          assignment.roleId,
          assignment.grantedBy,
          assignment.tenantId
        );

        if (success) {
          result.success.push(`${assignment.userId}:${assignment.roleId}`);
        } else {
          result.failed.push({
            userId: assignment.userId,
            roleId: assignment.roleId,
            error: 'Role assignment failed'
          });
        }
      } catch (error) {
        result.failed.push({
          userId: assignment.userId,
          roleId: assignment.roleId,
          error: error instanceof Error ? error.message : 'Unknown error'
        });
      }
    }

    return result;
  }

  // Audit and reporting
  getRoleAuditTrail(userId: string): UserRole[] {
    return this.getUserRoles(userId).sort((a, b) => b.grantedAt.getTime() - a.grantedAt.getTime());
  }

  getUsersWithRole(roleId: string): string[] {
    const users: string[] = [];
    
    for (const [userId, userRoles] of this.userRoles.entries()) {
      if (userRoles.some(ur => ur.roleId === roleId)) {
        users.push(userId);
      }
    }

    return users;
  }

  // Export/Import
  exportRoles(): Record<string, Role> {
    return Object.fromEntries(this.roles.entries());
  }

  importRoles(roles: Record<string, Role>): { success: string[]; failed: string[] } {
    const result = { success: [] as string[], failed: [] as string[] };

    for (const [roleId, role] of Object.entries(roles)) {
      try {
        // Validate role
        RoleSchema.parse(role);
        
        // Don't overwrite system roles
        if (Object.values(SystemRoles).includes(roleId as any)) {
          result.failed.push(`${roleId}: System role cannot be overwritten`);
          continue;
        }

        this.roles.set(roleId, role);
        this.customRoles.set(roleId, role);
        result.success.push(roleId);
      } catch (error) {
        result.failed.push(`${roleId}: ${error instanceof Error ? error.message : 'Invalid role format'}`);
      }
    }

    return result;
  }
}

// Export singleton instance
export const rbacService = RBACService.getInstance();

// Export types - already exported above